package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import io.github.koalaplot.core.bar.BarScope
import io.github.koalaplot.core.bar.DefaultBar

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HoverTooltip(
    text: String,
    modifier: Modifier = Modifier,
    textStyle: TextStyle = MaterialTheme.typography.bodyMedium,
    content: @Composable () -> Unit,
) {
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        modifier = modifier,
        tooltip = {
            PlainTooltip {
                Text(text = text, style = textStyle)
            }
        },
        state = rememberTooltipState(),
    ) {
        content()
    }
}

@Composable
fun ChartCard(
    title: String? = null,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            if (title != null) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                )
            }
            content()
        }
    }
}

data class LegendEntry(
    val color: Color,
    val label: String,
)

@Composable
fun SeriesLegend(
    entries: List<LegendEntry>,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        entries.forEach { entry ->
            LegendItem(color = entry.color, label = entry.label)
        }
    }
}

@Composable
private fun LegendItem(
    color: Color,
    label: String,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Box(modifier = Modifier.size(12.dp).background(color, AppShapes.small))
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
fun LinePointLabel(
    text: String,
    color: Color,
    above: Boolean = true,
) {
    Box(contentAlignment = Alignment.Center) {
        Box(
            modifier =
                Modifier
                    .size(8.dp)
                    .background(color, CircleShape),
        )
        Text(
            text = text,
            style = MaterialTheme.typography.bodyMedium,
            color = color,
            maxLines = 1,
            modifier = Modifier.offset(y = if (above) (-18).dp else 18.dp),
        )
    }
}

/**
 * Barra com o valor exibido na ponta (acima na vertical, ao lado na horizontal), quando a barra
 * renderizada for grande o suficiente para caber o rótulo.
 */
@Composable
fun BarScope.ValueBar(
    color: Color,
    label: String,
    horizontal: Boolean,
    negative: Boolean = false,
    labelInside: Boolean = false,
    labelColor: Color = color,
) {
    val textMeasurer = rememberTextMeasurer()
    val style = MaterialTheme.typography.labelSmall.copy(color = labelColor)
    val layout = remember(label, style) { textMeasurer.measure(AnnotatedString(label), style) }
    Box(Modifier.fillMaxSize()) {
        DefaultBar(color = color)
        Canvas(Modifier.fillMaxSize()) {
            val topLeft =
                if (horizontal) {
                    // Fora da ponta da barra: à direita quando positiva, à esquerda quando negativa.
                    val x = if (negative) -layout.size.width - 6.dp.toPx() else size.width + 6.dp.toPx()
                    Offset(x, (size.height - layout.size.height) / 2f)
                } else {
                    val x = (size.width - layout.size.width) / 2f
                    val y =
                        when {
                            labelInside && size.height >= layout.size.height + 12.dp.toPx() -> 6.dp.toPx()
                            negative -> size.height + 6.dp.toPx()
                            else -> -layout.size.height - 6.dp.toPx()
                        }
                    Offset(x, y)
                }
            drawText(layout, topLeft = topLeft)
        }
    }
}
